import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import NunitoText from "../general/NunitoText";
import ReusableAutocompleteWithID from "../general/ReusableAutocompleteWithID";
import ReusableButton from "../general/ReusableButton";
import { useSnackbar } from "notistack";
import { showWarningSwal } from "../../functions/alert";
import { showNotification } from "../../functions/snackbar";
import { gradeOptions as defaultGradeOptions } from "../../functions/general";
import {
  buildLanguagesQuery,
  extractAssignmentFilterOptions,
  toAssignmentSelectOption,
} from "../../functions/assignmentFilterOptions";
import {
  getAssignmentStoreFilterOptions,
  getCompetitions,
} from "../../functions/getData";
import { exportContestPaper } from "../../functions/postData";
import { isAdmin } from "../../functions/checkrole";

const rowStyle = { marginBottom: 24 };
const formBoxStyle = {
  backgroundColor: "#FFF",
  borderRadius: 12,
  boxShadow: "0 0 3px #9E9E9E",
  padding: 24,
  marginBottom: 24,
};

const userOrganizationId = (user) => {
  const o = user?.organization;
  if (o && typeof o === "object" && o.id != null) return o.id;
  if (typeof o === "number" || typeof o === "string") return o;
  return user?.organization_id ?? "";
};

export default function NewExportForm({ setLoading, onCreated }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const user = useSelector((state) => state.user);
  const countryStoreOptions = useSelector(
    (state) => state.countryOptions || []
  );

  const preferredOrgId = useMemo(() => userOrganizationId(user), [
    user?.organization,
    user?.organization_id,
  ]);

  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [gradeFilterOptions, setGradeFilterOptions] = useState([]);

  /** Org id used only for the same `get_filter=languages` query as Assign Translation. */
  const [filterOrgId, setFilterOrgId] = useState("");

  const [competition, setCompetition] = useState("");
  const [countryId, setCountryId] = useState(
    isAdmin() ? "" : user?.country_id || ""
  );
  const [languages, setLanguages] = useState([]);
  const [grade, setGrade] = useState("");

  const effectiveCountryId = useMemo(() => {
    if (isAdmin()) return countryId;
    return user?.country_id || "";
  }, [countryId, user?.country_id]);

  // —— Competition: same flow as AddAssigneeModal (store-filter-options + fallback). ——
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAssignmentStoreFilterOptions(
          "?get_filter=competition"
        );
        if (cancelled) return;
        let list = extractAssignmentFilterOptions(res, [
          "competition",
          "competitions",
        ]);
        if (!list.length) {
          const fb = await getCompetitions("", undefined).catch(() => null);
          if (cancelled) return;
          if (Array.isArray(fb)) list = fb;
          else
            list = extractAssignmentFilterOptions(fb, [
              "competition",
              "competitions",
            ]);
        }
        setCompetitionOptions(
          list
            .map(toAssignmentSelectOption)
            .filter(
              (o) =>
                o.id != null &&
                o.id !== "" &&
                String(o.option).trim() !== ""
            )
        );
      } catch {
        if (cancelled) return;
        try {
          const fb = await getCompetitions("", undefined);
          if (cancelled) return;
          const list = Array.isArray(fb)
            ? fb
            : extractAssignmentFilterOptions(fb, [
                "competition",
                "competitions",
              ]);
          setCompetitionOptions(
            list
              .map(toAssignmentSelectOption)
              .filter(
                (o) =>
                  o.id != null &&
                  o.id !== "" &&
                  String(o.option).trim() !== ""
              )
          );
        } catch {
          setCompetitionOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // —— Country (admin): same store-filter cascade as AddAssigneeModal. ——
  useEffect(() => {
    if (!isAdmin()) return;
    if (!competition) {
      setCountryOptions([]);
      setCountryId("");
      return;
    }
    setCountryId("");
    setFilterOrgId("");
    setLanguages([]);
    setGrade("");
    getAssignmentStoreFilterOptions(
      `?get_filter=country&competition=${competition}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, [
          "country",
          "countries",
        ]);
        if (list.length) {
          setCountryOptions(list.map(toAssignmentSelectOption));
        } else {
          setCountryOptions(
            countryStoreOptions.map((c) => ({
              id: c.id,
              option: c.display_name,
            }))
          );
        }
      })
      .catch(() => {
        setCountryOptions(
          countryStoreOptions.map((c) => ({
            id: c.id,
            option: c.display_name,
          }))
        );
      });
  }, [competition, countryStoreOptions]);

  // —— Pick organization id for language filter (not shown; same API as assignee). ——
  useEffect(() => {
    if (!competition || !effectiveCountryId) {
      setFilterOrgId("");
      setLanguages([]);
      setGrade("");
      setLanguageOptions([]);
      setGradeFilterOptions([]);
      return;
    }
    setFilterOrgId("");
    setLanguages([]);
    setGrade("");
    setLanguageOptions([]);
    setGradeFilterOptions([]);

    getAssignmentStoreFilterOptions(
      `?get_filter=organization&competition=${competition}&country=${effectiveCountryId}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, [
          "organization",
          "organizations",
        ]).map(toAssignmentSelectOption);
        const match = list.find(
          (o) => String(o.id) === String(preferredOrgId)
        );
        const picked = match?.id ?? list[0]?.id ?? "";
        setFilterOrgId(picked || "");
      })
      .catch(() => setFilterOrgId(""));
  }, [competition, effectiveCountryId, preferredOrgId]);

  // —— Languages: same URL shape as AddAssigneeModal (first org id). ——
  useEffect(() => {
    if (!competition || !effectiveCountryId || !filterOrgId) {
      setLanguageOptions([]);
      setLanguages([]);
      setGrade("");
      setGradeFilterOptions([]);
      return;
    }
    getAssignmentStoreFilterOptions(
      `?get_filter=languages&competition=${competition}&country=${effectiveCountryId}&organization=${filterOrgId}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, [
          "languages",
          "language",
        ]);
        setLanguageOptions(list.map(toAssignmentSelectOption));
      })
      .catch(() => setLanguageOptions([]));
  }, [competition, effectiveCountryId, filterOrgId]);

  // —— Grades: same `get_filter=grades` + `languages[]=` pattern as AddAssigneeModal. ——
  useEffect(() => {
    if (
      !competition ||
      !effectiveCountryId ||
      !filterOrgId ||
      !languages.length
    ) {
      setGradeFilterOptions([]);
      setGrade("");
      return;
    }
    const langQuery = buildLanguagesQuery(languages);
    getAssignmentStoreFilterOptions(
      `?get_filter=grades&competition=${competition}&country=${effectiveCountryId}&organization=${filterOrgId}&${langQuery}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, ["grades", "grade"]);
        if (list.length) {
          setGradeFilterOptions(list.map(toAssignmentSelectOption));
        } else {
          setGradeFilterOptions(
            defaultGradeOptions.map((g) => ({ id: g.id, option: g.name }))
          );
        }
      })
      .catch(() => {
        setGradeFilterOptions(
          defaultGradeOptions.map((g) => ({ id: g.id, option: g.name }))
        );
      });
    setGrade("");
  }, [competition, effectiveCountryId, filterOrgId, languages]);

  const onSubmit = () => {
    let warning = "";
    if (!competition) warning += "Please select a competition.<br>";
    if (!languages.length)
      warning += "Please select at least one language.<br>";
    if (!grade) warning += "Please select a grade.<br>";
    if (isAdmin() && !countryId)
      warning += "Please select a country.<br>";
    if (!effectiveCountryId)
      warning += "Missing country context for this export.<br>";
    if (!filterOrgId)
      warning +=
        "No organization is available for the selected competition and country, so language options could not be loaded.<br>";
    if (warning) return showWarningSwal(warning);

    const payload = {
      competition_id: competition,
      grade,
      language_ids: languages.map(String),
      country_id: effectiveCountryId,
    };

    setLoading(true);
    exportContestPaper(payload)
      .then((d) => {
        setLoading(false);
        if (d?.status && ![200, 201].includes(Number(d.status))) return;
        showNotification(
          "success",
          d?.message || "Export started",
          enqueueSnackbar,
          closeSnackbar
        );
        onCreated && onCreated(d);
      })
      .catch(() => setLoading(false));
  };

  const cascadeReady = Boolean(
    competition && effectiveCountryId && filterOrgId
  );

  return (
    <Box style={formBoxStyle}>
      <NunitoText
        value="New Export"
        fontSize={24}
        fontWeight={700}
        color="#5E75C3"
        marginBottom={16}
      />

      <Grid container justifyContent="space-between" style={rowStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Competition*"
          state={competition}
          setState={setCompetition}
          required
          options={competitionOptions}
        />
        {isAdmin() ? (
          <ReusableAutocompleteWithID
            type="withLabel"
            width={500}
            placeholder="Country*"
            state={countryId}
            setState={setCountryId}
            required
            options={countryOptions}
            grayedOut={!competition}
            readOnly={!competition}
          />
        ) : (
          <div style={{ width: 500 }} />
        )}
      </Grid>

      <Grid container justifyContent="space-between" style={rowStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Languages*"
          state={languages}
          setState={setLanguages}
          required
          multiple
          options={languageOptions}
          grayedOut={!cascadeReady}
          readOnly={!cascadeReady}
        />
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Grade*"
          state={grade}
          setState={setGrade}
          required
          options={gradeFilterOptions}
          grayedOut={!languages.length}
          readOnly={!languages.length}
        />
      </Grid>

      <Grid container justifyContent="flex-end" style={{ marginTop: 16 }}>
        <ReusableButton
          text="Start Export"
          bgColor="#5E75C3"
          fontSize={18}
          height={50}
          width={220}
          onClick={onSubmit}
        />
      </Grid>
    </Box>
  );
}
